import React, { useEffect, useMemo, useState } from "react";
import originalConfig from './vir_subject_enrollment_forms.json';
import DynamicFormEngine from "../dynamicFormEngine";
import {
  bloqcibeApi,
  useGetSubjectDetailQuery,
  useGetTrialSiteInfoQuery,
  useSaveSubjectEnrollInfoMutation,
  useUploadSubjectFileMutation,
  useUploadSubjectSignatureMutation,
} from "../../store/slices/apiSlice";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogTitle,
  Typography,
  TextField,
  DialogContent,
  DialogActions,
  Box,
  MenuItem,
  Select,
  FormControl,
  Divider,
  useTheme,
} from "@mui/material";
import RightArrow from "../../components/icons/RightArrow";
import {
  base64ImageToBlob,
  createNewFields,
  getUserRole,
  getSubjectMasterId,
} from "../util";
import { base64ToArrayBuffer } from "../../components/common/DocumentUpload";
import { mergeCustomFields } from "../../util/util";
import { AUDIO_LANGUAGES } from "../../util/constants";
import { setConsentLanguage } from "../../store/slices/subjectSlice";
import { selectCurrentUser } from "../../store/slices/authSlice";
import { openBackdropLoading } from "../../store/slices/applicationSlice";
import DynamicFormAction from "../dynamicFormEngine/FormEntity/DynamicFormAction";
import { openMessage } from "../../store/slices/showMessageSlice";
import { setQueries, setQueriesLoading, clearQueries, selectHasOpenQueries, selectQueriesByFieldKey, selectRefetchTrigger } from '../../store/slices/querySlice';
import SubjectParticipantIdHeading from '../../components/common/SubjectParticipantIdHeading';
import BackButton from "../../components/common/BackButton";

const local = false;

const EnrollSubject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { trialId, trialSiteId, siteInitStep, subjectMasterId } = useParams();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const subjectLoggedIn = useSelector((state) => state.auth.subjectLoggedIn);
  const [originalFormConfig, setOriginalFormConfig] = useState(null);
  const [formConfig, setFormConfig] = useState(local ? originalConfig.steps.find((_obj) => _obj.key == siteInitStep) : null);
  // const [formConfig, setFormConfig] = useState(originalConfig.steps.find((_obj) => _obj.key == siteInitStep));
  const enrolmentDocumentKey = useSelector(
    (state) => state?.trial?.trialSiteDetail?.siteTrialData?.trial?.eConsentForm
  );
  const [siteId, setSiteId] = useState(null);
  const {user: loggedInUser, userData} = useSelector((state) => state.auth);
  const consentLanguage = useSelector((state) => state.subject.consentLanguage);
  const [downloadSubjectSignature] = bloqcibeApi.endpoints.downloadSubjectSignature.useLazyQuery();
  const [getEConsentFormConfig, { data: formData }] = bloqcibeApi.endpoints.getEConsentFormConfig.useLazyQuery();
  const [uploadSubjectSignature] = useUploadSubjectSignatureMutation();
  const [uploadSubjectFile] = useUploadSubjectFileMutation();
  const [getSubjectDashboardDetail] = bloqcibeApi.endpoints.getSubjectDashboardDetail.useLazyQuery();
  const [downloadSubjectFile] = bloqcibeApi.endpoints.downloadSubjectFile.useLazyQuery();
  const [getSubjectDetail, { data: subjectDetailWithSteps }] = bloqcibeApi.endpoints.getSubjectDetail.useLazyQuery();
  const { data: trialSiteinfo } = useGetTrialSiteInfoQuery({
    sponsorId: sponsorId,
    trialId: trialId,
    siteTrialId: trialSiteId,
  });
  const [stepStatusData, setStepStatusData] = useState([]);
  const [formAnswers, setFormAnswers] = useState();
  const [dynamicFormAction, setDynamicFormAction] = useState(DynamicFormAction.NA);
  const [getLibraryForm] = bloqcibeApi.endpoints.getLibraryForm.useLazyQuery();
  const [getSubjectAnswers] = bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();
  const [saveSubjectEnrollInfo] = useSaveSubjectEnrollInfoMutation();
  const [signatureData, setSignatureData] = useState([]);
  const [getQueries] = bloqcibeApi.endpoints.getQueries.useLazyQuery();
  
  // Track selected section/subSection for query fetching
  const [selectedSectionKey, setSelectedSectionKey] = useState();
  const [selectedSubSectionKey, setSelectedSubSectionKey] = useState();
  
  // Query selectors from Redux
  const hasOpenQueries = useSelector(selectHasOpenQueries);
  const queriesByFieldKey = useSelector(selectQueriesByFieldKey);
  const refetchTrigger = useSelector(selectRefetchTrigger);
  const [labelModel, setLabelModel] = useState({
    open: false,
    fieldKey: '',
  });
  const [label, setLabel] = useState('');
  const [customFieldConfig, setCustomFieldConfig] = useState([]);

  useEffect(() => {
    (async () => {
      if (subjectMasterId) {
        await getSubjectDetail(subjectMasterId);
      }
    })();
  }, [subjectMasterId]);

  useEffect(() => {
    if (subjectDetailWithSteps) {
      const steps = _.sortBy(subjectDetailWithSteps?.stepStatus, 'order');
      const statusData = steps.find((_obj) => _obj.stepKey === siteInitStep);
      setStepStatusData(statusData);
    }
  }, [subjectDetailWithSteps]);

  const onFormChanged = async (sectionKey, subSectionKey) => {
    setSelectedSectionKey(sectionKey);
    setSelectedSubSectionKey(subSectionKey);
    if (subjectMasterId) {
      const _answers = await getSubjectAnswers({
        sponsorId,
        trialId,
        siteId: trialSiteinfo?.siteTrialData?.siteId,
        payload: {
          subjectMasterId: subjectMasterId,
          stepKey: siteInitStep,
          sectionKey: sectionKey,
          subSectionKey: subSectionKey,
        },
      });
      setFormAnswers(_answers.data);
    }
  };

  // Initialize selectedSectionKey when formConfig loads (for forms without sections or on initial load)
  useEffect(() => {
    if (formConfig && !selectedSectionKey) {
      // If form has sections, use first section key
      if (formConfig.sections && formConfig.sections.length > 0) {
        setSelectedSectionKey(formConfig.sections[0].key);
        if (formConfig.sections[0].subTabs && formConfig.sections[0].subTabs.length > 0) {
          setSelectedSubSectionKey(formConfig.sections[0].subTabs[0].key);
        }
      } else {
        // For forms without sections, use the step/form key itself
        setSelectedSectionKey(formConfig.key || siteInitStep);
      }
    }
  }, [formConfig, selectedSectionKey, siteInitStep]);

  // Fetch queries for the selected form section
  useEffect(() => {
    const fetchQueries = async () => {
      const currentSiteId = siteId || trialSiteinfo?.siteTrialData?.siteId;
      if (!sponsorId || !trialId || !currentSiteId || !subjectMasterId || !siteInitStep) {
        return;
      }
      
      dispatch(setQueriesLoading(true));
      try {
        const result = await getQueries({
          sponsorId,
          trialId,
          siteId: currentSiteId,
          stepKey: siteInitStep,
          sectionKey: selectedSectionKey || '',
          subSectionKey: selectedSubSectionKey || '',
          subjectMasterId,
          page: 1,
          limit: 100,
          active: true,
        });

        if (result.data) {
          const fetchedQueries = result.data.data || [];
          dispatch(setQueries(fetchedQueries));
        } else {
          dispatch(setQueries([]));
        }
      } catch (error) {
        console.error('Error fetching queries:', error);
        dispatch(setQueries([]));
      }
    };

    fetchQueries();
  }, [sponsorId, trialId, siteId, trialSiteinfo?.siteTrialData?.siteId, subjectMasterId, siteInitStep, selectedSectionKey, selectedSubSectionKey, dispatch, getQueries, refetchTrigger]);

  // Clear queries when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearQueries());
    };
  }, [dispatch]);

  const userSiteTrialRole = useMemo(() => {
    return getUserRole(loggedInUser, trialId, trialSiteinfo?.siteTrialData?.site?.id, userData);
  }, [loggedInUser, trialSiteinfo, userData]);

  useEffect(() => {
    (async () => {
      if (enrolmentDocumentKey) {
        setSignatureData([]);
        const formDetailsData = await getLibraryForm(
          `${process.env.REACT_APP_API_ENDPOINT_URL}form-library/library/bloqcube/${enrolmentDocumentKey}`
          // `http://localhost:3003/form-library/library/bloqcube/${enrolmentDocumentKey}`
        );
        if (formDetailsData.data) {
          const config = local ? originalConfig : formDetailsData.data;
          setOriginalFormConfig(config);
          // setOriginalFormConfig(originalConfig);
        } else if (formDetailsData.error) {
          // setOriginalFormConfig(originalConfig);
          console.error('UNABLE TO GET FORM DETAILS');
        }
      }
    })();
  }, [siteInitStep, enrolmentDocumentKey]);
  useEffect(() => {
    (async () => {
      if (trialSiteinfo) {
        setSiteId(trialSiteinfo.siteTrialData.siteId);
        if (subjectMasterId) {
          const _answers = await getSubjectAnswers({
            sponsorId,
            trialId,
            siteId: trialSiteinfo.siteTrialData.siteId,
            payload: {
              subjectMasterId: subjectMasterId,
              stepKey: siteInitStep,
            },
          });
          // Only update formConfig if it already exists, don't overwrite with null
          if (formConfig) {
            const valChange = JSON.parse(JSON.stringify(formConfig));
            setFormConfig(valChange);
          }
          setFormAnswers(_answers.data);
        }
      }
    })();
  }, [trialSiteinfo, trialId, userData]);

  useEffect(() => {
    (async () => {
      if (siteId && originalFormConfig && enrolmentDocumentKey) {
        const formDetailsData = await getEConsentFormConfig({
          sponsorId,
          trialId,
          siteId,
          documentKey: enrolmentDocumentKey,
          payload: {
            subjectMasterId: subjectMasterId,
          },
        });
        if (formDetailsData.data) {
          setCustomFieldConfig(formDetailsData.data);
          let _tempConfig = { ...originalFormConfig };
          const mergedConfig = mergeCustomFields(_tempConfig, formDetailsData.data);
          const foundStep = mergedConfig.steps?.find((_obj) => _obj.key === siteInitStep);
          if (foundStep) {
            setFormConfig(foundStep);
          } else {
            console.error(`Step with key "${siteInitStep}" not found in mergedConfig.steps`);
          }
        } else if (formDetailsData.error) {
          console.error('UNABLE TO GET FORM DETAILS');
        } else {
          const foundStep = originalFormConfig.steps?.find((_obj) => _obj.key === siteInitStep);
          if (foundStep) {
            setFormConfig(foundStep);
          } else {
            console.error(`Step with key "${siteInitStep}" not found in originalFormConfig.steps`);
          }
        }
      }
    })();
  }, [siteId, originalFormConfig, siteInitStep, enrolmentDocumentKey]);

  const findObjectByValue = async (obj, targetValue, path = [], keys) => {
    for (const key in obj) {
      if (obj[key] === targetValue) {
        let configFields = obj?.addFields;
        let needLabelField = _.find(configFields, {
          needLabel: true,
        });
        if (configFields && needLabelField && !label) {
          setLabelModel({
            open: true,
            fieldKey: obj.key,
          });
          return null;
        } else if (configFields && needLabelField && label) {
          const attachedLabel = configFields.map((field) => {
            if (field.needLabel === true) {
              return {
                ...field,
                label: label,
              };
            } else {
              return field;
            }
          });
          const newFields = createNewFields(attachedLabel, obj.fields.length + 1);
          // Deep clone the config to ensure React state updates
          const updatedConfig = JSON.parse(JSON.stringify(formConfig));
          // Find the same object in the cloned config by path and update its fields
          let current = updatedConfig;
          for (const p of path) {
            if (current && current[p]) {
              current = current[p];
            }
          }
          if (current && current.fields && Array.isArray(current.fields)) {
            current.fields = [...(current.fields || []), ...newFields];
          }
          // Track this added group locally with an id and metadata
          const newGroupId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
          const newFieldKeys = (newFields || []).map((f) => f.key);
          setCustomFieldConfig((prev) => [
            ...(prev || []),
            {
              id: newGroupId,
              stepKey: siteInitStep,
              sectionKey: keys.sectionKey,
              subSectionKey: keys.subSectionKey,
              categoryKey: keys.categoryKey,
              customFields: newFieldKeys,
            },
          ]);
          setFormConfig(updatedConfig);
          break;
        } else if (configFields) {
          const newFields = createNewFields(configFields, obj.fields.length + 1);
          // Deep clone the config to ensure React state updates
          const updatedConfig = JSON.parse(JSON.stringify(formConfig));
          // Find the same object in the cloned config by path and update its fields
          let current = updatedConfig;
          for (const p of path) {
            if (current && current[p]) {
              current = current[p];
            }
          }
          if (current && current.fields && Array.isArray(current.fields)) {
            current.fields = [...(current.fields || []), ...newFields];
          }
          // Track this added group locally with an id and metadata
          const newGroupId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
          const newFieldKeys = (newFields || []).map((f) => f.key);
          setCustomFieldConfig((prev) => [
            ...(prev || []),
            {
              id: newGroupId,
              stepKey: siteInitStep,
              sectionKey: keys.sectionKey,
              subSectionKey: keys.subSectionKey,
              categoryKey: keys.categoryKey,
              customFields: newFieldKeys,
            },
          ]);
          setFormConfig(updatedConfig);
          break;
        } else {
          console.error("'newFields' key not found ");
        }
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Continue searching in the nested object
        const result = await findObjectByValue(obj[key], targetValue, [...path, key], keys);
        if (result) {
          return result;
        }
      }
    }
    // Value not found in the current object
    return null;
  };

  const addNewField = async (parentKey, keys) => {
    const valChange = JSON.parse(JSON.stringify(formConfig));
    await findObjectByValue(valChange, parentKey, [], {
      documentKey: originalFormConfig?.documentKey,
      ...keys,
    });
  };

  const removeCustomField = async (field, keys) => {
    dispatch(openBackdropLoading({ backdropLoading: true }));

    console.log(keys);

    const cloned = JSON.parse(JSON.stringify(formConfig));

    const removeByKeyRecursive = (node, targetKey) => {
      if (!node) return false;

      if (Array.isArray(node)) {
        for (let i = node.length - 1; i >= 0; i--) {
          const item = node[i];
          if (item && item.key === targetKey) {
            node.splice(i, 1);
            return true;
          }
          if (removeByKeyRecursive(item, targetKey)) return true;
        }
        return false;
      }

      if (typeof node === 'object') {
        if (node.fields && removeByKeyRecursive(node.fields, targetKey)) return true;
        if (node.categories && removeByKeyRecursive(node.categories, targetKey)) return true;
        if (node.subTabs && removeByKeyRecursive(node.subTabs, targetKey)) return true;
        if (node.sections && removeByKeyRecursive(node.sections, targetKey)) return true;
      }
      return false;
    };

    // Find matching groups by keys context
    const matchingGroups = (customFieldConfig || []).filter(
      (config) =>
        config?.stepKey === siteInitStep &&
        config?.sectionKey === keys?.sectionKey &&
        config?.subSectionKey === keys?.subSectionKey &&
        config?.categoryKey === keys?.categoryKey
    );

    const deleteGroup = _.find(
      matchingGroups,
      (group) => Array.isArray(group?.customFields) && group.customFields.includes(field.key)
    );

    if (deleteGroup) {
      // Remove all fields that belong to this group
      for (const keyToRemove of deleteGroup.customFields) {
        while (removeByKeyRecursive(cloned, keyToRemove)) {}
      }
      // Remove group tracking entry
      setCustomFieldConfig((prev) => (prev || []).filter((g) => g.id !== deleteGroup.id));
    } else {
      // Fallback to single field removal
      removeByKeyRecursive(cloned, field.key);
    }

    setFormConfig(cloned);
    dispatch(openBackdropLoading({ backdropLoading: false }));
  };

  const handleSave = async (payload) => {
    try {
      const data = { ...payload };

      if (subjectMasterId) {
        data.subjectMasterId = subjectMasterId;
      }

      data.documentKey = originalFormConfig.documentKey;
      data.documentVersion = originalFormConfig.documentVersion;
      data.crfDocumentKey = trialSiteinfo?.siteTrialData?.trial?.crfForm;
      data.crfDocumentVersion = '1.0';

      dispatch(openBackdropLoading({ backdropLoading: true }));

      // Save subject enrollment info
      const response = await saveSubjectEnrollInfo({
        trialId: trialId,
        siteId: siteId,
        sponsorId: sponsorId,
        payload: data,
      });

      // Upload signatures if subjectMasterId is not present
      if (!subjectMasterId) {
        for (const signChild of signatureData) {
          const formData = new FormData();
          for (const [key, value] of Object.entries(signChild)) {
            if (key === 'file') {
              formData.append(key, base64ImageToBlob(value));
            } else {
              formData.append(key, value);
            }
          }
          formData.append('subjectMasterId', response.data?.subjectMasterId);

          // Upload each signature
          await uploadSubjectSignature({
            trialId: trialId,
            siteId: siteId,
            sponsorId: sponsorId,
            payload: formData,
          });
        }
      }

      // Refresh dashboard if subject is logged in
      if (subjectLoggedIn) {
        const subjectId = getSubjectMasterId(loggedInUser, userData);
        if (subjectId) {
          await getSubjectDashboardDetail(subjectId);
        }
      }

      if (response.data) {
        // Pass API response message if present, otherwise use default
        const apiMessage = response.data?.message;
        const screeningFailure = response.data?.screeningFailure;
        const onHold = response.data?.onHold;
        if (screeningFailure) {
          navigate(-1);
          setDynamicFormAction(
            apiMessage
              ? { type: DynamicFormAction.FORM_SAVE_FAILURE, message: apiMessage }
              : DynamicFormAction.FORM_SAVE_FAILURE
          );
        } else if (onHold) {
          navigate(-1);
          setDynamicFormAction(
            apiMessage
              ? { type: DynamicFormAction.FORM_SAVE_WARNING, message: apiMessage }
              : DynamicFormAction.FORM_SAVE_WARNING
          );
        } else {
          setDynamicFormAction(
            apiMessage
              ? { type: DynamicFormAction.FORM_SAVE_SUCCESS, message: apiMessage }
              : DynamicFormAction.FORM_SAVE_SUCCESS
          );
        }
      } else if (response?.error) {
        if (response.error?.data?.message.includes('already exists')) {
          setDynamicFormAction(DynamicFormAction.NA);
          dispatch(openBackdropLoading({ backdropLoading: false }));
          dispatch(openMessage({ messageSeverity: 'error', message: response?.error?.data?.message }));
        } else {
          // Pass API error message if present
          const errorMessage = response.error?.data?.message;
          setDynamicFormAction(
            errorMessage
              ? { type: DynamicFormAction.FORM_SAVE_FAILURE, message: errorMessage }
              : DynamicFormAction.FORM_SAVE_FAILURE
          );
        }
      }

      return response;
    } catch (error) {
      console.error('Failed to save:', error);
      setDynamicFormAction(DynamicFormAction.FORM_SAVE_FAILURE);
      return Promise.reject(error);
    }
  };

  const handleSaveSignature = async (keys, fieldName, setFieldValueFun) => {
    if (subjectMasterId) {
      const formData = new FormData();
      formData.append('subjectMasterId', subjectMasterId);
      formData.append('file', base64ImageToBlob(keys.file));
      formData.append('stepKey', keys.stepKey);
      formData.append('sectionKey', keys.sectionKey);
      formData.append('subSectionKey', keys.subSectionKey);
      formData.append('categoryKey', keys.categoryKey);
      formData.append('fieldKey', keys.fieldKey);
      const result = await uploadSubjectSignature({
        payload: formData,
        sponsorId: sponsorId,
        trialId: trialId,
        siteId: siteId,
      });
      setFieldValueFun(fieldName, result.data.s3Key);
    } else {
      const _signData = {
        file: keys.file,
        stepKey: keys.stepKey,
        sectionKey: keys.sectionKey,
        subSectionKey: keys.subSectionKey,
        categoryKey: keys.categoryKey,
        fieldKey: keys.fieldKey,
      };
      const _SignatureData = [...signatureData];
      _SignatureData.push(_signData);
      setSignatureData(_SignatureData);
      setFieldValueFun(fieldName, "testSign");
    }
  };
  const handleDownloadSignature = async (s3Key, setSign) => {
    const result = await downloadSubjectSignature({
      s3Key: s3Key,
    });
    if (result.data) {
      const byteArray = base64ToArrayBuffer(result.data);
      const rr = new Blob([byteArray], { type: 'application/octet-stream' });
      const reader = new FileReader();
      reader.readAsDataURL(rr);
      reader.onloadend = function () {
        const base64data = reader.result;
        setSign(base64data);
      };
      reader.onerror = function () {
        setSign(null);
      };
    } else {
      console.log('Failed to load signature');
      setSign(null);
    }
  };
  const handleUploadFile = async (keys, fieldName, setFieldValueFun) => {
    const formData = new FormData();
    formData.append('file', keys.file);
    formData.append('subjectMasterId', subjectMasterId);
    formData.append('stepKey', keys.stepKey);
    formData.append('sectionKey', keys.sectionKey);
    formData.append('subSectionKey', keys.subSectionKey ? keys.subSectionKey : '');
    formData.append('categoryKey', keys.categoryKey);
    formData.append('fieldKey', keys.fieldKey);
    const result = await uploadSubjectFile({
      payload: formData,
      sponsorId: sponsorId,
      trialId: trialId,
      siteId: siteId,
    });
    setFieldValueFun(fieldName, result.data.s3Key, true);
  };
  const handleDownloadFile = async (s3Key, fileName, setSelectedFile) => {
    const result = await downloadSubjectFile({
      s3Key: s3Key,
    });
    if (result.data) {
      let byteArray = base64ToArrayBuffer(result.data);
      let a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(new Blob([byteArray], { type: 'application/octet-stream' }));
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setSelectedFile && setSelectedFile(new Blob([byteArray], { type: 'application/octet-stream' }));
    } else {
      console.log('Failed to load signature');
    }
  };
  const handleLanguageChange = (e) => {
    dispatch(setConsentLanguage(e.target.value));
  };

  const selectedFormStatus = useMemo(() => {
    if (siteInitStep && subjectDetailWithSteps) {
      const steps = _.sortBy(subjectDetailWithSteps?.stepStatus, 'order');
      const statusData = steps.find((_obj) => _obj.stepKey === siteInitStep);
      return statusData?.status;
    }
    return null;
  }, [siteInitStep, subjectDetailWithSteps]);

  // editAllowed - Comprehensive permission check matching SubjectDetailCRFInfo.jsx logic
  const editAllowed = useMemo(() => {
    // For new subjects (no subjectMasterId), always allow editing
    if (!subjectMasterId) {
      return true;
    }

    // Subject is On_Hold - no edit allowed
    if (subjectDetailWithSteps && subjectDetailWithSteps?.status === 'On_Hold') {
      return false;
    }

    // Block editing for terminal statuses
    if (
      stepStatusData &&
      ['Withdrawal', 'Enrollment_Failure', 'Screening_Failure', 'On_Hold'].includes(stepStatusData.status)
    ) {
      return false;
    }

    if (userSiteTrialRole && stepStatusData && stepStatusData.userAccess) {
      const userAccess = JSON.parse(stepStatusData.userAccess);
      const editAccess = userAccess.edit;
      const isAccess =
        typeof editAccess !== 'string'
          ? _.find(editAccess, (access) => {
              return access === userSiteTrialRole;
            })
          : editAccess === userSiteTrialRole;

      // Check readOnlyEdit configuration from step config
      const readOnlyEditConfig = formConfig?.readOnlyEdit;

      // Allow edit if user has edit access and no readOnlyEdit config
      if (isAccess && !readOnlyEditConfig) {
        return true;
      }

      if (readOnlyEditConfig) {
        const currentStatus = stepStatusData.status;

        // Check granular status-based role access
        if (readOnlyEditConfig.statusAccess && readOnlyEditConfig.statusAccess[currentStatus]) {
          const statusRoles = readOnlyEditConfig.statusAccess[currentStatus];
          const hasStatusAccess = Array.isArray(statusRoles)
            ? statusRoles.includes(userSiteTrialRole)
            : statusRoles === userSiteTrialRole;

          if (hasStatusAccess) {
            return true;
          }
        }

        // Check legacy format: allowApplicableStatus + roles
        const allowApplicableStatus = readOnlyEditConfig.allowApplicableStatus || [];
        const readOnlyEditRoles = readOnlyEditConfig.roles || readOnlyEditConfig.access?.edit || [];

        if (allowApplicableStatus.length > 0) {
          const isStatusAllowed = allowApplicableStatus.includes(currentStatus);
          const hasReadOnlyEditAccess = Array.isArray(readOnlyEditRoles)
            ? readOnlyEditRoles.includes(userSiteTrialRole)
            : readOnlyEditRoles === userSiteTrialRole;

          if (isStatusAllowed && hasReadOnlyEditAccess) {
            return true;
          }
        }
      }

      // Allow edit if there are open queries on the form (for resolving queries)
      if (hasOpenQueries) {
        const currentStatus = stepStatusData.status;
        const verifyAccess = userAccess.verify;
        const hasVerifyAccess = _.find(verifyAccess, (access) => access === userSiteTrialRole);

        // Allow editing for users with verify access when there are open queries
        if (hasVerifyAccess) {
          return true;
        }

        // Check readOnlyEdit for open queries
        const readOnlyEditConfig = formConfig?.readOnlyEdit;
        if (readOnlyEditConfig) {
          // Specific query resolution roles
          if (readOnlyEditConfig.queryResolutionAccess) {
            const queryRoles = readOnlyEditConfig.queryResolutionAccess;
            const hasQueryAccess = Array.isArray(queryRoles)
              ? queryRoles.includes(userSiteTrialRole)
              : queryRoles === userSiteTrialRole;

            if (hasQueryAccess) {
              return true;
            }
          }

          // Check if user has access for current status via statusAccess
          if (readOnlyEditConfig.statusAccess && readOnlyEditConfig.statusAccess[currentStatus]) {
            const statusRoles = readOnlyEditConfig.statusAccess[currentStatus];
            const hasStatusAccess = Array.isArray(statusRoles)
              ? statusRoles.includes(userSiteTrialRole)
              : statusRoles === userSiteTrialRole;

            if (hasStatusAccess) {
              return true;
            }
          }

          // Fallback to legacy roles
          const readOnlyEditRoles = readOnlyEditConfig.roles || readOnlyEditConfig.access?.edit || [];
          const hasReadOnlyEditAccess = Array.isArray(readOnlyEditRoles)
            ? readOnlyEditRoles.includes(userSiteTrialRole)
            : readOnlyEditRoles === userSiteTrialRole;

          if (hasReadOnlyEditAccess) {
            return true;
          }
        }
      }

      return false;
    }
    
    // If no stepStatusData yet (still loading), allow editing for existing subjects
    // This prevents blocking the form before data loads
    if (subjectMasterId && !stepStatusData) {
      return true;
    }
    
    return false;
  }, [subjectMasterId, userSiteTrialRole, stepStatusData, subjectDetailWithSteps, hasOpenQueries, formConfig]);
  return (
    <>
      <Box sx={{ position: 'relative', display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <BackButton backHandler={() => navigate(-1)} />
        <SubjectParticipantIdHeading />
        <Box sx={{ minWidth: 0 }} />
      </Box>
      {subjectLoggedIn && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
            <Typography sx={{ fontWeight: 600, color: '#2196f3' }}>Audio Language</Typography>
            <Box sx={{ width: '55%' }}>
              <FormControl fullWidth>
                <Select value={consentLanguage} onChange={handleLanguageChange}>
                  {AUDIO_LANGUAGES.map((option) => {
                    return (
                      <MenuItem key={option.locale} value={option.locale}>
                        {option.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Divider sx={{ marginY: 2 }} />
        </>
      )}
      {formConfig && (
        <DynamicFormEngine
          // hideTab={formConfig && formConfig?.sections?.length >=2 }
          formConfig={formConfig}
          AddNewField={addNewField}
          userSiteTrialRole={userSiteTrialRole}
          readOnly={!editAllowed}
          handleUploadFile={handleUploadFile}
          handleDownloadFile={handleDownloadFile}
          handleSave={(payload) => {
            handleSave(payload);
          }}
          {...(subjectLoggedIn && { showSkeleton: selectedFormStatus == 'Completed' })}
          {...((selectedFormStatus === 'Withdrawal' || selectedFormStatus === 'Enrollment_Failure') && {
            showSkeleton: true,
          })}
          handleSaveSignature={handleSaveSignature}
          handleDownloadSignature={handleDownloadSignature}
          formAnswers={formAnswers}
          removeCustomField={removeCustomField}
          stepStatusData={stepStatusData}
          onFormChanged={onFormChanged}
          dynamicFormAction={dynamicFormAction}
          setDynamicFormAction={setDynamicFormAction}
          queriesByFieldKey={queriesByFieldKey}
          hasOpenQueries={hasOpenQueries}
        />
      )}
      <Dialog
        onClose={() => {
          setLabelModel({
            ...labelModel,
            open: false,
          });
        }}
        maxWidth={'sm'}
        open={labelModel.open}
        sx={{
          dialogPaper: {
            minHeight: '80vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>Enter label</DialogTitle>
        <DialogContent sx={{ minHeight: '10vh', minWidth: '40vW' }}>
          <TextField label="Label" value={label} sx={{ mt: 2 }} onChange={(e) => setLabel(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelModel({ ...labelModel, open: false })}>Cancel</Button>
          <Button
            onClick={() => {
              addNewField(labelModel.fieldKey);
              setLabelModel({ ...labelModel, open: false, fieldKey: '' });
              setLabel('');
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnrollSubject;
